import { forwardRef, type ButtonHTMLAttributes } from 'react';

import { cn } from '@/shared/cn';

/**
 * Small icon-only button used for hover-revealed secondary controls in the
 * sidebar (pin, delete, move-out, project-add). Lifted verbatim from the
 * sidebar's local helper; hit area moved from `h-[22px] w-[22px]` to the
 * on-grid `h-6 w-6` per the audit's "Standardise icon-button hit areas"
 * recommendation.
 */
export type IconButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  label: string;
  emphasised?: boolean;
  danger?: boolean;
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton(
    { children, label, danger, emphasised, className, type, ...rest },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type={type ?? 'button'}
        aria-label={label}
        title={label}
        className={cn(
          'inline-flex h-6 w-6 items-center justify-center rounded-full transition-[color,background-color,transform] active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--color-accent-500)_50%,transparent)]',
          emphasised
            ? 'text-accent-600'
            : 'text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100',
          danger
            ? 'hover:bg-[color-mix(in_srgb,var(--color-accent-600)_10%,transparent)] hover:text-accent-600'
            : 'hover:bg-[color-mix(in_srgb,var(--color-zinc-900)_5%,transparent)] dark:hover:bg-white/5',
          className,
        )}
        {...rest}
      >
        {children}
      </button>
    );
  },
);
